import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { EventType } from "../models/EventType";
import { EventSubtype } from "../models/EventSubtype";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { clean } from "../utils/utils";
import { EventCategoryService } from "../services/EventCategoryService";
import { EventTypeService } from "../services/EventTypeService";
import { EventSubtypeService } from "../services/EventSubtypeService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { EventCategory } from "../models/EventCategory";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface EventTypeContextProps {
  eventCategories: EventCategory[];
  eventTypes: EventType[];
  eventSubtypes: EventSubtype[];
  loading: Loadings;
  error: Errors;
  createEventCategory: (data: Omit<EventCategory, "_id">) => Promise<string>;
  createEventType: (data: Omit<EventType, "_id">) => Promise<string>;
  createEventSubtype: (data: Omit<EventSubtype, "_id">) => Promise<string>;
  updateEventCategory: (
    id: string,
    data: Partial<EventCategory>,
  ) => Promise<void>;
  updateEventType: (id: string, data: Partial<EventType>) => Promise<void>;
  updateEventSubtype: (
    id: string,
    data: Partial<EventSubtype>,
  ) => Promise<void>;
  deleteEventCategory: (id: string) => Promise<void>;
  deleteEventType: (id: string) => Promise<void>;
  deleteEventSubtype: (id: string) => Promise<void>;
}

const EventTypeContext = createContext<EventTypeContextProps | undefined>(
  undefined,
);

export const useEventType = (): EventTypeContextProps => {
  const context = useContext(EventTypeContext);
  if (!context) {
    throw new Error("useEventType must be used within an EventTypeProvider");
  }
  return context;
};

export const EventTypeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { currentMatterId } = useMatter();
  const [eventCategories, setEventCategories] = useState<EventCategory[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [eventSubtypes, setEventSubtypes] = useState<EventSubtype[]>([]);
  const [loading, setLoading] = useState<Loadings>({
    list: true,
    details: true,
  });
  const [error, setError] = useState<Errors>({});

  const fetchAll = async () => {
    if (!user || !currentMatterId) return;
    setError({});

    try {
      const [categories, types, subtypes] = await Promise.all([
        EventCategoryService.getEventCategories(),
        EventTypeService.getEventTypes(),
        EventSubtypeService.getEventSubtypes(),
      ]);

      setEventCategories(categories);
      setEventTypes(types);
      setEventSubtypes(subtypes);
    } catch (err: any) {
      setError({ list: err, details: err });
      console.error("Failed to load event types or subtypes:", err);
    } finally {
      setLoading({ list: false, details: false });
    }
  };

  useEffect(() => {
    setLoading({ list: true, details: true });
    fetchAll();
  }, [user, currentMatterId]);

  useSocketEventByIds("eventcategories:changed", ["*"], fetchAll);
  useSocketEventByIds("eventtypes:changed", ["*"], fetchAll);
  useSocketEventByIds("eventsubtypes:changed", ["*"], fetchAll);

  const createEventCategory = async (data: Omit<EventCategory, "_id">) => {
    const newCategory = {
      ...data,
      createdBy: user!.uid,
      updatedBy: user!.uid,
    };
    const id = await EventCategoryService.createEventCategory(
      currentMatterId!,
      newCategory,
    );
    return id;
  };

  const createEventType = async (data: Omit<EventType, "_id">) => {
    const newType = {
      ...data,
      createdBy: user!.uid,
      updatedBy: user!.uid,
    };
    const id = await EventTypeService.createEventType(
      currentMatterId!,
      newType,
    );
    return id;
  };

  const createEventSubtype = async (data: Omit<EventSubtype, "_id">) => {
    const newSubtype = {
      ...data,
    };
    const id = await EventSubtypeService.createEventSubtype(
      currentMatterId!,
      newSubtype,
    );
    return id;
  };

  const updateEventCategory = async (
    id: string,
    data: Partial<EventCategory>,
  ) => {
    await EventCategoryService.updateEventCategory(id, clean(data));
  };

  const updateEventType = async (id: string, data: Partial<EventType>) => {
    await EventTypeService.updateEventType(id, clean(data));
  };

  const updateEventSubtype = async (
    id: string,
    data: Partial<EventSubtype>,
  ) => {
    await EventSubtypeService.updateEventSubtype(id, clean(data));
  };

  const deleteEventCategory = async (id: string) => {
    await EventCategoryService.deleteEventCategory(id);
    setEventCategories((prev) => prev.filter((cat) => cat._id !== id));
  };

  const deleteEventType = async (id: string) => {
    await EventTypeService.deleteEventType(id);
    setEventTypes((prev) => prev.filter((type) => type._id !== id));
  };

  const deleteEventSubtype = async (id: string) => {
    await EventSubtypeService.deleteEventSubtype(id);
    setEventSubtypes((prev) => prev.filter((subtype) => subtype._id !== id));
  };

  return (
    <EventTypeContext.Provider
      value={{
        eventTypes,
        eventSubtypes,
        loading,
        error,
        eventCategories,
        createEventCategory,
        createEventType,
        createEventSubtype,
        updateEventCategory,
        updateEventType,
        updateEventSubtype,
        deleteEventCategory,
        deleteEventType,
        deleteEventSubtype,
      }}
    >
      {children}
    </EventTypeContext.Provider>
  );
};
